import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';
import Spinner from '../components/Spinner';
import { Note, Shift, User, UserRole } from '../interfaces';
import './Profile.css';
import Select from 'react-select';
import SuccessNotification from '../components/notifications/SuccessNotification';
import CircularProgress from '@mui/material/CircularProgress';
import { format } from 'date-fns';
import EditNotesPopup from '../components/popups/EditNotesPopup';
import ResetUserPasswordPopup from '../components/popups/ResetUserPasswordPopup';
import DeactivateUserPopup from '../components/popups/DeactivateUserPopup';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import DeleteConfirmation from '../components/DeleteConfirmationPopup';
import { DeviceService } from '../DeviceService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Property } from '../Toolbar';

const Profile: React.FC = () => {
    const [, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [showSpinner, setShowSpinner] = useState(false);
    const [user, setUser] = useState<User | null>();
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [totalPaidRevenue, setTotalPaidRevenue] = useState(0);
    const [userRole, setUserRole] = useState<UserRole | null>();

    const [totalNumOfReservations, setTotalNumOfReservations] = useState(0);
    const [selectedTab, setSelectedTab] = useState('actions');
    const [imageLoading, setimageLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [canUserBeDeactivated, setCanUserBeDeactivated] = useState(false);
    const [headerTitle, setHeaderTitle] = useState('');
    const [content, setContent] = useState('');
    const genderOptions = [
        { value: 'Male', label: 'Male' },
        { value: 'Female', label: 'Female' },
        { value: 'Other', label: 'Other' }
    ];
    const [newNoteText, setNewNoteText] = useState('');
    const notesTabs = [
        { label: 'Active Notes', value: 'Active Notes' },
        { label: 'Archived Notes', value: 'Archived Notes' },
    ];
    const [selectedNotesTab, setSelectedNotesTab] = useState('Active Notes');
    const [noteOptionsOpen, setNoteOptionsOpen] = useState<string | null>(null);
    const noteOptionsRef = useRef(null);
    const userOptionsRef = useRef<HTMLDivElement | null>(null);
    const userOptionsToggleRef = useRef<HTMLButtonElement | null>(null);
    const [property, setProperty] = useState<Property | null>(null);
    const receiptRef = useRef<HTMLDivElement>(null);
    const [editNoteDrawerOpen, setEditNoteDrawerOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState<Note | null>(null);
    const [showUserActions, setShowUserActions] = useState(false);
    const [showResetPasswordPopup, setShowResetPasswordPopup] = useState(false);
    const [showDeactivateUserPopup, setShowDeactivateUserPopup] = useState(false);
    const [currentShift, setCurrentShift] = useState<Shift | null>(null);
    const [shiftLoading, setShiftLoading] = useState(false);
    const [myShiftData, setMyShiftData] = useState<Shift | null>(null);
    const [showMyShiftPopup, setShowMyShiftPopup] = useState(false);
    const [myShiftLoading, setMyShiftLoading] = useState(false);
    const navigate = useNavigate();
    const [hasEndedMyShift, setHasEndedMyShift] = useState(false);


    const { logout, hasPerm } = useAuth();

    const handleLogout = async (clockOutSuccess: boolean = false) => {
        try {
            await DeviceService.unregisterDevice();
            console.log('Device unregistered');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Authentication/logout`, {
                method: 'POST',
                credentials: 'include',
            });
    
            if (response.ok) {
                localStorage.removeItem('currentProperty');
                logout();
                localStorage.removeItem('deviceId');
                localStorage.removeItem('propertyId');
                navigate('/login', { state: { clockOutSuccess } });
            } else {
                console.error('Logout failed:', response.statusText);
            }
        } catch (error) {
            console.error('Failed to logout:', error);
        }
    };

    /**
     * Pulls the current user's shift figures and shows the X-Report.
     *
     * `reprint` separates pulling the report again from ending the shift. An X-Report is a
     * non-final, repeatable read, so it can be taken as often as needed while the shift is
     * active — but only the first pull records the end-of-shift event. Saving again would
     * append a second UserXReports entry (UserService.SaveUserXReport appends, it does not
     * upsert) and log another "X-Report generated" activity for one shift ending, and it
     * would leave two candidate timestamps behind the "Your Shift Ended" line below.
     */
    const fetchMyShiftData = async (reprint = false) => {
        try {
            setMyShiftLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Shift/current/my-data`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setMyShiftData(data);
                if (!reprint) {
                    await saveUserXReport(data);
                    setHasEndedMyShift(true);
                }
                setShowMyShiftPopup(true);
            } else {
                setHeaderTitle("Info");
                setContent("No active shift or no orders found for your shift.");
                setShowSuccess(true);
                setTimeout(() => setShowSuccess(false), 3000);
            }
        } catch (error) {
            console.error('Failed to fetch my shift data:', error);
        } finally {
            setMyShiftLoading(false);
        }
    };



    const startShift = async () => {
        try {
            setShiftLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Shift/start`, {
                method: 'POST',
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setCurrentShift(data);
                setHeaderTitle("Success!");
                setContent("Shift started successfully.");
                setShowSuccess(true);
                setTimeout(() => setShowSuccess(false), 3000);
            } else {
                const errorText = await response.text();
                console.error('Failed to start shift:', errorText);
            }
        } catch (error) {
            console.error('Error starting shift:', error);
        } finally {
            setShiftLoading(false);
        }
    };




    useEffect(() => {
        const handleResize = () => {
            console.log("onresize: innerWidth: " + window.innerWidth + "innerHeight: " + innerHeight)
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.onresize = handleResize;

        return () => {
            window.onresize = null;
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const updateSize = () => {
            console.log("update size")
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);



    const fetchTotalNumOfReservations = async (email: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/User/get-num-of-reservations-by-email/` + email, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setTotalNumOfReservations(data);
            } else {
                console.error('Failed to fetch total number of reservations for user');
            }
        } catch (error) {
            console.error('Failed to fetch total number of reservations for user');
        } finally {
        }
    };

    const fetchRevenue = async (email: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/User/get-total-revenue-by-email/` + email, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setTotalRevenue(data);
            } else {
                console.error('Failed to fetch total revenue for user');
            }
        } catch (error) {
            console.error('Failed to fetch total revenue for user');
        } finally {
        }
    };

    const fetchPaidRevenue = async (email: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/User/get-total-paid-revenue-by-email/` + email, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setTotalPaidRevenue(data);
            } else {
                console.error('Failed to fetch total paid revenue for user');
            }
        } catch (error) {
            console.error('Failed to fetch total paid revenue for user');
        } finally {
        }
    };

    const fetchUserRoleById = async (roleId: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/UserRole/` + roleId, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setUserRole(data);
            } else {
                console.error('Failed to fetch user role');
            }
        } catch (error) {
            console.error('Failed to fetch user role');
        } finally {
        }
    };

    const saveUserXReport = async (shiftData: Shift) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/User/save-xreport`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    shiftId: shiftData.shiftId,
                    shiftStartDate: shiftData.startDate,
                    shiftEndDate: shiftData.endDate,
                    grossSales: shiftData.grossSales ?? 0,
                    discounts: shiftData.discounts ?? 0,
                    returns: shiftData.returns ?? 0,
                    netSales: shiftData.netSales ?? 0,
                    taxes: shiftData.taxes ?? 0,
                    serviceCharge: shiftData.serviceCharge ?? 0,
                    totalRevenue: shiftData.totalRevenue ?? 0,
                    expectedCash: shiftData.expectedCash ?? 0,
                    creditCards: shiftData.creditCards ?? 0,
                    roomCharges: shiftData.roomCharges ?? 0,
                    numberOfReceipts: shiftData.numberOfReceipts ?? 0,
                    numberOfVoidOrders: shiftData.numberOfVoidOrders ?? 0,
                    firstOrderNumber: shiftData.firstOrderNumber,
                    lastOrderNumber: shiftData.lastOrderNumber,
                }),
            });

            if (!response.ok) {
                console.error('Failed to save X-Report');
            }
        } catch (error) {
            console.error('Error saving X-Report:', error);
        }
    };

    useEffect(() => {
        setShowSpinner(true);
        const fetchUserData = async () => {
            try {
                const storedProperty = localStorage.getItem('currentProperty');
                if (storedProperty) {
                    setProperty(JSON.parse(storedProperty));
                }

                const response = await fetch(`${process.env.REACT_APP_API_URL}/User/get-by-email`, {
                    credentials: 'include',
                });
                if (response.ok) {
                    const data = await response.json();
                    setUser(data);

                    // Everything below depends only on the user we just loaded, never on
                    // each other, so they go out together. Awaiting them one at a time
                    // made the page cost six round trips end-to-end instead of one.
                    const loadShift = async () => {
                        const shiftResponse = await fetch(`${process.env.REACT_APP_API_URL}/Shift/current`, {
                            credentials: 'include',
                        });
                        if (shiftResponse.ok) {
                            const shiftData = await shiftResponse.json();
                            setCurrentShift(shiftData);

                            if (data.userXReports && shiftData?.shiftId) {
                                const hasReportForCurrentShift = data.userXReports.some(
                                    (report: any) => report.shiftId === shiftData.shiftId
                                );
                                setHasEndedMyShift(hasReportForCurrentShift);
                            }
                        } else {
                            setCurrentShift(null);
                        }
                    };

                    const loadCanBeDeactivated = async () => {
                        const deactivateResponse = await fetch(`${process.env.REACT_APP_API_URL}/User/can-be-deactivated/` + data.id, {
                            credentials: 'include',
                        });
                        if (deactivateResponse.ok) {
                            setCanUserBeDeactivated(await deactivateResponse.json());
                        } else {
                            console.error('Failed to fetch user data');
                        }
                    };

                    // allSettled, not all: one failing statistic must not blank the whole
                    // page — each loader already handles its own error.
                    await Promise.allSettled([
                        fetchPaidRevenue(data.email),
                        fetchRevenue(data.email),
                        fetchTotalNumOfReservations(data.email),
                        fetchUserRoleById(data.roleId),
                        loadShift(),
                        loadCanBeDeactivated(),
                    ]);
                } else {
                    console.error('Failed to fetch user data');
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            } finally {
                setShowSpinner(false);
            }
        };

        fetchUserData();
    }, []);

    const hasIncompleteLatestShift = () => {
        if (user != null) {
            if (user.userShiftHistory.length === 0) return false;
            else {
                const sortedShifts = [...user.userShiftHistory].sort((a, b) => {
                    const dateA = a.clockInTime ? new Date(a.clockInTime).getTime() : 0;
                    const dateB = b.clockInTime ? new Date(b.clockInTime).getTime() : 0;
                    return dateB - dateA;
                });

                const latestShift = sortedShifts[0];

                return latestShift.clockInTime !== null && latestShift.clockOutTime === null;
            }
        }

        return false;
    }


    const formatDateReport = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toISOString().slice(0, 16).replace('T', ' ');
    };

    const formatShiftId = (id: string | null | undefined) => {
        if (!id) return '000000';
        return id.toString().padStart(6, '0');
    };

    const formatDateForFilename = () => {
        const now = new Date();
        return now.toISOString().slice(0, 10);
    };

    const handlePrintUserReport = () => {
        window.print();
    };

    const handleSaveUserReport = async () => {
        if (!receiptRef.current || !myShiftData) return;

        try {
            const canvas = await html2canvas(receiptRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const imgX = (pdfWidth - imgWidth * ratio) / 2;
            const imgY = 10;

            pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);

            const filename = `User-X-Report_${formatShiftId(myShiftData.shiftId)}_${formatDateForFilename()}.pdf`;
            pdf.save(filename);
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const formatCurrency = (value: number | null | undefined) => {
        const num = value ?? 0;
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
    };


    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setimageLoading(true);
        const file = event.target.files ? event.target.files[0] : null;

        if (file) {
            const filePath = file.name;
            const fileContentType = file.type;

            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onloadend = async () => {
                const base64File = typeof reader.result === 'string' ? reader.result.split(',')[1] : '';

                try {
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/User/upload-profile-img/`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            file: base64File,
                            contentType: fileContentType,
                            path: filePath
                        })
                    });
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }

                    const result = await response.text();
                    if (user) {
                        setUser((prev) => ({
                            ...prev as User,
                            profilePicture: result,
                        }));
                    }

                    setimageLoading(false);
                } catch (error) {
                    console.error("Error when uploading file: ", error);
                }
            };
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUser(prev => ({
            ...prev as User,
            [name]: value
        }));
    };

    const clockIn = async () => {
        try {
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/User/user-clock-in/` + user?.id, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setUser(data);
            } else {
                console.error('Failed to fetch user data');
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        } finally {
            setShowSpinner(false);
        }
    }

    // Add helper function to check permission
    const hasPermission = (permissionName: string, action: 'canView' | 'canCreate' | 'canEdit' | 'canDelete') => {
        if (!userRole?.permissions) return false;
        const permission = userRole.permissions.find(p => p.name === permissionName);
        return permission ? permission[action] : false;
    };



    const clockOut = async () => {
        try {
            if (hasPermission('Allow cash drawer management', 'canCreate')) {
                navigate('/close-user-shift');
                return;
            }
    
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/User/user-clock-out/` + user?.id, {
                credentials: 'include',
            });
            if (response.ok) {
                await handleLogout(true);
            } else {
                console.error('Failed to clock out');
                setHeaderTitle("Error");
                setContent("Failed to clock out. Please try again.");
                setShowSuccess(true);
                setTimeout(() => setShowSuccess(false), 3000);
            }
        } catch (error) {
            console.error('Error clocking out:', error);
            setHeaderTitle("Error");
            setContent("An error occurred while clocking out.");
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        } finally {
            setShowSpinner(false);
        }
    };

    const [confirmConfig, setConfirmConfig] = useState<null | {
        title: string;
        content: string;
        onConfirm: () => void;
    }>(null);

    const openConfirm = (title: string, content: string, onConfirm: () => void) => {
        setConfirmConfig({ title, content, onConfirm });
    };

    const saveUser = async () => {
        try {
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/User/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: user?.id,
                    propertyId: user?.propertyId || '00000000-0000-0000-0000-000000000000',
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    email: user?.email,
                    phone: user?.phone,
                    gender: user?.gender,
                    streetAddress: user?.streetAddress,
                    aptSuiteFloor: user?.aptSuiteFloor,
                    profileImageUrl: user?.profilePicture,
                    city: user?.city,
                    state: user?.state,
                    postalCode: user?.postalCode,
                    notes: user?.notes,
                    userShiftHistory: user?.userShiftHistory,
                    pin: user?.pin,
                    userName: user?.userName,
                    passwordHsh: user?.passwordHsh,
                    passwordSalt: user?.passwordSalt,
                    defaultFloor: user?.defaultFloor,
                    defaultTable: user?.defaultTable,
                    twoFactorSecret: user?.twoFactorSecret,
                    roleId: user?.roleId,
                    profilePicture: user?.profilePicture,
                    isActive: user?.isActive,
                    numberOfVoidedOrders: user?.numberOfVoidedOrders
                }),
                credentials: 'include',
            });

            if (response) {
                setShowSpinner(false);
                setHeaderTitle("Success!");
                setContent("User was saved successfully.")
                setShowSuccess(true);
                setTimeout(() => {
                    setShowSuccess(false);
                }, 3000);
            }
        } catch (error) {
            console.error(error);
        } finally {
        }
    };

    const saveUpdatedUser = async (updatedUser: User) => {
        try {
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/User/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: updatedUser?.id,
                    propertyId: updatedUser?.propertyId || '00000000-0000-0000-0000-000000000000',
                    firstName: updatedUser?.firstName,
                    lastName: updatedUser?.lastName,
                    email: updatedUser?.email,
                    phone: updatedUser?.phone,
                    gender: updatedUser?.gender,
                    streetAddress: updatedUser?.streetAddress,
                    aptSuiteFloor: updatedUser?.aptSuiteFloor,
                    profileImageUrl: updatedUser?.profilePicture,
                    city: updatedUser?.city,
                    state: updatedUser?.state,
                    postalCode: updatedUser?.postalCode,
                    notes: updatedUser?.notes,
                    userShiftHistory: updatedUser?.userShiftHistory,
                    pin: updatedUser?.pin,
                    userName: updatedUser?.userName,
                    passwordHsh: updatedUser?.passwordHsh,
                    passwordSalt: updatedUser?.passwordSalt,
                    defaultFloor: updatedUser?.defaultFloor,
                    defaultTable: updatedUser?.defaultTable,
                    twoFactorSecret: updatedUser?.twoFactorSecret,
                    roleId: updatedUser?.roleId,
                    profilePicture: updatedUser?.profilePicture
                }),
                credentials: 'include',
            });

            if (response) {
                setShowSpinner(false);
                setHeaderTitle("Success!");
                setContent("User was saved successfully.")
                setUser(updatedUser);
                setShowSuccess(true);
                setTimeout(() => {
                    setShowSuccess(false);
                }, 3000);
            }
        } catch (error) {
            console.error(error);
        } finally {
        }
    };

    const handleGenderChange = (selectedOption: { value: string, label: string } | null) => {
        setUser(prev => ({
            ...prev as User,
            gender: selectedOption ? selectedOption.value : ''
        }));
    };

    const generateUUID = () => {
        let d = new Date().getTime();
        if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
            d += performance.now();
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (d + Math.random() * 16) % 16 | 0;
            d = Math.floor(d / 16);
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    };

    const handleSaveNote = async () => {
        const email = localStorage.getItem('userName');

        if (user) {
            if (!user?.notes) {
                user.notes = [];
            }

            user.notes?.push({
                id: generateUUID(),
                isArchived: false,
                date: new Date(),
                note: newNoteText,
                addedBy: email || '',
            });

            setNewNoteText('');
            await saveUser();
        }
    };

    const formatNoteDate = (date: Date) => {
        return format(date, 'dd/MM/yyyy');
    };

    const formatTime = (date: Date) => {
        return format(date, 'hh:mm');
    };

    const handleToggleOptions = (noteId: string) => {
        setNoteOptionsOpen(noteOptionsOpen === noteId ? null : noteId);
    };

    const handleArchiveNote = async (noteId: string) => {
        if (user) {
            const updatedUserNotes = user.notes?.map(note =>
                note.id === noteId
                    ? { ...note, isArchived: true }
                    : note
            );

            const updatedUser = {
                ...user,
                notes: updatedUserNotes
            };
            await saveUpdatedUser(updatedUser);
            setNoteOptionsOpen(null);
        }
    };

    const handleEditNote = (note: Note) => {
        setEditNoteDrawerOpen(true);
        setSelectedNote(note);
        setNoteOptionsOpen(null);
    };


    const handleSaveEditNote = async (updatedNoteText: string) => {
        if (!selectedNote) return;

        if (user) {
            const noteExistsInGuestNotes = user.notes?.some(note => note.id === selectedNote.id);

            if (noteExistsInGuestNotes) {
                const updatedNotes = user.notes.map(note =>
                    note.id === selectedNote.id ? { ...note, note: updatedNoteText } : note
                );
                const updatedUser = {
                    ...user,
                    notes: updatedNotes
                };
                await saveUpdatedUser(updatedUser);

            }
            setSelectedNote(null);
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if
            (userOptionsRef.current &&
            !userOptionsRef.current.contains(event.target as Node) &&
            userOptionsToggleRef.current &&
            !userOptionsToggleRef.current.contains(event.target as Node)
        ) {
            setShowUserActions(false);
        }
    };

    useEffect(() => {
        if (showUserActions) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserActions]);

    const handleSavePassword = async (pass: string) => {
        try {
            setShowResetPasswordPopup(false);
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/User/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user?.id,
                    newPassword: pass,
                    emailSiginInfoToMe: false,
                    requireChangePassowrd: false,
                }),
                credentials: 'include',
            });

            if (response) {
                setShowSpinner(false);
                setHeaderTitle("Success!");
                setContent("User was saved successfully.")
                setShowSuccess(true);
                setTimeout(() => {
                    setShowSuccess(false);
                }, 3000);
            }
        } catch (error) {
            console.error(error);
        } finally {
        }
    };



    const handleDeactivateUser = async () => {
        try {
            setShowDeactivateUserPopup(false);
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/User/deactivate/` + user?.id, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });

            if (response) {
                setShowSpinner(false);
                localStorage.setItem('displayDeactivatedUser', JSON.stringify(true));
                await handleLogout();
            }
        } catch (error) {
            console.error(error);
        } finally {
        }
    };

    return (
        <div className='profile-page profile-view ps-scope'>
            <div className='d-flex justify-content-between pv-head'>
                <div className='pv-title'>{user?.firstName} {user?.lastName} <span className='pv-role'>{userRole?.name}</span></div>
                {
                    selectedTab == 'actions' &&
                    <div className='d-flex gap-2'>
                        {
                            hasIncompleteLatestShift() ?
                                <button className='pv-btn pv-btn--danger'
                                    onClick={() =>
                                        openConfirm(
                                            'Clock Out',
                                            'Are you sure you want to clock out?',
                                            clockOut
                                        )
                                    }>Clock Out</button>
                                :
                                <button
                                    className='pv-btn pv-btn--success'
                                    disabled={!currentShift}
                                    title={!currentShift ? 'No active shift. A manager must start a shift first.' : ''}
                                    onClick={() =>
                                        openConfirm(
                                            'Clock In',
                                            'Are you sure you want to clock in?',
                                            clockIn
                                        )
                                    }
                                >
                                    {!currentShift ? 'No Active Shift' : 'Clock In'}
                                </button>
                        }
                        {
                            currentShift ?
                                <>
                                    {/* Stays visible for the whole shift. It used to unmount once
                                        hasEndedMyShift flipped, which left no way to pull the
                                        X-Report a second time — and because the flag is rehydrated
                                        from user.userXReports on load, the button never came back.
                                        Once the shift has ended the press is a reprint: no confirm
                                        (there is nothing left to confirm) and no second record. */}
                                    {hasPerm('pos-app.shift.close') && (
                                        <button
                                            className='pv-btn pv-btn--ghost'
                                            disabled={myShiftLoading}
                                            title={hasEndedMyShift ? 'View or print your X-Report again' : ''}
                                            onClick={() =>
                                                hasEndedMyShift
                                                    ? fetchMyShiftData(true)
                                                    : openConfirm(
                                                        'End Shift',
                                                        'Are you sure you want to end your shift? This will generate your shift report.',
                                                        () => fetchMyShiftData()
                                                    )
                                            }
                                        >
                                            {myShiftLoading ? 'Loading...' : 'End Shift'}
                                        </button>
                                    )}
                                    {hasPerm('pos-app.shift.end-day') && (
                                        <button
                                            className='pv-btn pv-btn--danger'
                                            onClick={() => navigate('/close-shift')}
                                        >
                                            End Day
                                        </button>
                                    )}
                                </>
                                :
                                <button
                                    className='pv-btn pv-btn--success'
                                    disabled={shiftLoading}
                                    onClick={() =>
                                        openConfirm(
                                            'Start Shift',
                                            'Are you sure you want to start a new shift?',
                                            startShift
                                        )
                                    }
                                >
                                    {shiftLoading ? 'Loading...' : 'Start Shift'}
                                </button>
                        }
                    </div>
                }
                {
                    selectedTab === 'details' &&
                    <div className='d-flex gap-2' style={{ position: 'relative' }}>
                        <button className='pv-btn pv-btn--ghost' onClick={() => setShowUserActions(!showUserActions)} ref={userOptionsToggleRef}>
                            User Actions
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 7.5L10 12.5L15 7.5" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                        {
                            showUserActions &&
                            <div className='options-menu' ref={userOptionsRef} style={{ position: 'absolute', top: 44, height: 'fit-content', right: 0, minWidth: '100%', zIndex: 100 }}>
                                <button className='pv-menuitem' onClick={() => { setShowResetPasswordPopup(true); setShowUserActions(false) }}>Reset Password</button>
                                <button className='pv-menuitem' onClick={() => { setShowDeactivateUserPopup(true); setShowUserActions(false) }}>Deactivate User</button>
                            </div>
                        }
                    </div>
                }

            </div>
            <div className='pv-tabs'>
                <button className={selectedTab == 'actions' ? "pv-tab is-on" : "pv-tab"} onClick={() => setSelectedTab('actions')}>Duty Actions</button>
                <button className={selectedTab == 'details' ? "pv-tab is-on" : "pv-tab"} onClick={() => setSelectedTab('details')}>Details</button>
                <button className={selectedTab == 'notes' ? "pv-tab is-on" : "pv-tab"} onClick={() => setSelectedTab('notes')}>Notes ({user?.notes ? user?.notes.length : 0})</button>
            </div>
            {
                selectedTab == 'actions' &&
                <>
                    <div className='pv-panel' style={{ marginTop: 'var(--space-5)' }}>
                        {currentShift && (
                            <div className='pv-shiftmeta'>
                                {hasEndedMyShift ? (
                                    <>
                                        <div className='pv-shiftmeta__row'>
                                            <strong>Your Shift Ended:</strong>{' '}
                                            {user?.userXReports?.find(r => r.shiftId === currentShift.shiftId)?.reportDate
                                                ? new Date(user.userXReports.find(r => r.shiftId === currentShift.shiftId)!.reportDate).toLocaleString()
                                                : new Date().toLocaleString()
                                            }
                                        </div>
                                        <div className='pv-shiftmeta__row'><strong>Property Shift Started By:</strong> {currentShift.startedByUserWithName}</div>
                                    </>
                                ) : (
                                    <>
                                        <div className='pv-shiftmeta__row'><strong>Current Shift Started:</strong> {new Date(currentShift.startDate).toLocaleString()}</div>
                                        <div className='pv-shiftmeta__row'><strong>Started By:</strong> {currentShift.startedByUserWithName}</div>
                                    </>
                                )}
                                <hr className='pv-divider' />
                            </div>
                        )}
                        <div className='pv-stats'>
                            <div className='pv-stat'>
                                <span className='pv-stat__k'>Revenue</span>
                                <span className='pv-stat__v'>{totalRevenue}</span>
                            </div>
                            <div className='pv-stat'>
                                <span className='pv-stat__k'>Paid Revenue</span>
                                <span className='pv-stat__v'>{totalPaidRevenue}</span>
                            </div>
                            <div className='pv-stat'>
                                <span className='pv-stat__k'>Voided Orders</span>
                                <span className='pv-stat__v'>{user?.numberOfVoidedOrders}</span>
                            </div>
                        </div>
                    </div>
                    <h5 className='pv-section-title'>User reports</h5>
                    <div className='pv-table'>
                        <div className='pv-reports__row pv-reports__row--head'>
                            <div className='pv-reports__h'>Report Name</div>
                            <div className='pv-reports__h'>Descriptions</div>
                            <div className='pv-reports__h'></div>
                        </div>
                        <div className='pv-reports__row'>
                            <div className='pv-reports__c pv-reports__c--name'>User Report X</div>
                            <div className='pv-reports__c'>A temporary transaction summary report used to review ongoing sales activity and cash flow during a specific shift or timeframe.</div>
                            <div className='pv-reports__c pv-reports__c--action'>
                                <button className='pv-linkbtn'>View</button>
                            </div>
                        </div>
                        <div className='pv-reports__row'>
                            <div className='pv-reports__c pv-reports__c--name'>User-Z Report</div>
                            <div className='pv-reports__c'>A final financial report that summarizes total revenue and all transactions completed within a full operational day.</div>
                            <div className='pv-reports__c pv-reports__c--action'>
                                <button className='pv-linkbtn'>View</button>
                            </div>
                        </div>
                    </div>
                </>
            }
            {
                selectedTab == 'details' &&
                <div className='two-col-layout mt-4 gap-3 flex-column-reverse flex-lg-row'>
                    <div className='pv-card pv-form w-100'>
                        <h6 className='pv-eyebrow'>CONTACT INFORMATION</h6>
                        <div className='pv-fieldgrid'>
                            <div className='pv-field'>
                                <label className='pv-label'>First Name</label>
                                <input
                                    type='text'
                                    className='pv-input'
                                    name="firstName"
                                    value={user?.firstName || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='pv-field'>
                                <label className='pv-label'>Last Name</label>
                                <input
                                    type='text'
                                    className='pv-input'
                                    name="lastName"
                                    value={user?.lastName || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='pv-field pv-field--full'>
                                <label className='pv-label'>Email Address</label>
                                <input
                                    type='text'
                                    className='pv-input'
                                    name="email"
                                    value={user?.email || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='pv-field'>
                                <label className='pv-label'>Phone</label>
                                <input
                                    type='text'
                                    className='pv-input'
                                    name="phone"
                                    value={user?.phone || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='pv-field pv-select'>
                                <label className='pv-label'>Gender</label>
                                <Select
                                    classNamePrefix="pvsel"
                                    value={genderOptions.find(option => option.value === user?.gender)}
                                    onChange={handleGenderChange}
                                    options={genderOptions}
                                    placeholder="Select Gender"
                                    isClearable
                                />
                            </div>
                        </div>

                        <h6 className='pv-eyebrow' style={{ marginTop: 'var(--space-5)' }}>ADDRESS</h6>
                        <div className='pv-fieldgrid'>
                            <div className='pv-field pv-field--full'>
                                <label className='pv-label'>Street Address</label>
                                <input
                                    type='text'
                                    className='pv-input'
                                    name="streetAddress"
                                    value={user?.streetAddress || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='pv-field pv-field--full'>
                                <label className='pv-label'>Apt, suite, floor etc.</label>
                                <input
                                    className='pv-input'
                                    name="aptSuiteFloor"
                                    value={user?.aptSuiteFloor || ''}
                                    onChange={handleInputChange}
                                    type='text'
                                />
                            </div>
                            <div className='pv-field'>
                                <label className='pv-label'>City</label>
                                <input
                                    className='pv-input'
                                    name="city"
                                    type='text'
                                    value={user?.city || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='pv-field'>
                                <label className='pv-label'>Province / State</label>
                                <input
                                    className='pv-input'
                                    name="state"
                                    type='text'
                                    value={user?.state || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className='pv-field'>
                                <label className='pv-label'>Postal / ZIP Code</label>
                                <input
                                    className='pv-input'
                                    name="postalCode"
                                    type='text'
                                    value={user?.postalCode || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        <div className='d-flex mt-4 justify-content-end'>
                            <button className='pv-btn pv-btn--primary' onClick={saveUser}>
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.83333 1.5V4.33333C4.83333 4.80004 4.83333 5.0334 4.92416 5.21166C5.00406 5.36846 5.13154 5.49594 5.28834 5.57584C5.4666 5.66667 5.69996 5.66667 6.16667 5.66667H11.8333C12.3 5.66667 12.5334 5.66667 12.7117 5.57584C12.8685 5.49594 12.9959 5.36846 13.0758 5.21166C13.1667 5.0334 13.1667 4.80004 13.1667 4.33333V2.33333M13.1667 16.5V11.1667C13.1667 10.7 13.1667 10.4666 13.0758 10.2883C12.9959 10.1315 12.8685 10.0041 12.7117 9.92416C12.5334 9.83333 12.3 9.83333 11.8333 9.83333H6.16667C5.69996 9.83333 5.4666 9.83333 5.28834 9.92416C5.13154 10.0041 5.00406 10.1315 4.92416 10.2883C4.83333 10.4666 4.83333 10.7 4.83333 11.1667V16.5M16.5 6.77124V12.5C16.5 13.9001 16.5 14.6002 16.2275 15.135C15.9878 15.6054 15.6054 15.9878 15.135 16.2275C14.6002 16.5 13.9001 16.5 12.5 16.5H5.5C4.09987 16.5 3.3998 16.5 2.86502 16.2275C2.39462 15.9878 2.01217 15.6054 1.77248 15.135C1.5 14.6002 1.5 13.9001 1.5 12.5V5.5C1.5 4.09987 1.5 3.3998 1.77248 2.86502C2.01217 2.39462 2.39462 2.01217 2.86502 1.77248C3.3998 1.5 4.09987 1.5 5.5 1.5H11.2288C11.6364 1.5 11.8402 1.5 12.0321 1.54605C12.2021 1.58688 12.3647 1.65422 12.5138 1.7456C12.682 1.84867 12.8261 1.9928 13.1144 2.28105L15.719 4.88562C16.0072 5.17387 16.1513 5.318 16.2544 5.48619C16.3458 5.63531 16.4131 5.79789 16.4539 5.96795C16.5 6.15976 16.5 6.36358 16.5 6.77124Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                Save Changes
                            </button>
                        </div>
                    </div>
                    <div className='d-flex flex-column w-100 gap-3'>
                        <div className='pv-card d-flex gap-3'>
                            {
                                imageLoading ?
                                    <div style={{ width: '80px' }} className='d-flex justify-content-center align-items-center'>
                                        <CircularProgress
                                            style={{
                                                width: 30,
                                                height: 30,
                                                zIndex: 3,
                                            }}
                                        />
                                    </div>
                                    :
                                    <>
                                        {
                                            user?.profilePicture && user?.profilePicture != '' ?
                                                <img src={user?.profilePicture} className='pv-avatar' />
                                                :
                                                <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <circle cx="50" cy="50" r="50" fill="#D9D9D9" />
                                                    <path d="M63.3332 65C63.3332 62.6741 63.3332 61.5111 63.0461 60.5648C62.3998 58.4341 60.7324 56.7667 58.6017 56.1204C57.6554 55.8333 56.4924 55.8333 54.1665 55.8333H45.8332C43.5072 55.8333 42.3443 55.8333 41.3979 56.1204C39.2673 56.7667 37.5999 58.4341 36.9536 60.5648C36.6665 61.5111 36.6665 62.6741 36.6665 65M57.4998 42.5C57.4998 46.6421 54.142 50 49.9998 50C45.8577 50 42.4998 46.6421 42.4998 42.5C42.4998 38.3579 45.8577 35 49.9998 35C54.142 35 57.4998 38.3579 57.4998 42.5Z" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                        }
                                    </>
                            }

                            <div className='d-flex flex-column'>
                                <label className='pv-label'>
                                    Your photo
                                </label>
                                <small>This will be displayed on your profile.</small>
                                <div className='d-flex mt-1 align-items-center'>
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8.41009 0.910826C8.73553 0.585389 9.26317 0.585389 9.5886 0.910826L13.7553 5.07749C14.0807 5.40293 14.0807 5.93057 13.7553 6.256C13.4298 6.58144 12.9022 6.58144 12.5768 6.256L9.83268 3.51193L9.83268 11.5001C9.83268 11.9603 9.45959 12.3334 8.99935 12.3334C8.53911 12.3334 8.16602 11.9603 8.16602 11.5001L8.16602 3.51193L5.42194 6.256C5.0965 6.58144 4.56886 6.58144 4.24343 6.256C3.91799 5.93057 3.91799 5.40293 4.24343 5.07749L8.41009 0.910826Z" fill="#1643E0" />
                                        <path d="M1.49935 10.6667C1.95959 10.6667 2.33268 11.0398 2.33268 11.5001V12.5001C2.33268 13.2139 2.33333 13.6991 2.36398 14.0742C2.39383 14.4396 2.44793 14.6264 2.51434 14.7567C2.67413 15.0703 2.9291 15.3253 3.2427 15.4851C3.37303 15.5515 3.55987 15.6056 3.92522 15.6355C4.30029 15.6661 4.78553 15.6667 5.49935 15.6667H12.4994C13.2132 15.6667 13.6984 15.6661 14.0735 15.6355C14.4388 15.6056 14.6257 15.5515 14.756 15.4851C15.0696 15.3253 15.3246 15.0703 15.4844 14.7567C15.5508 14.6264 15.6049 14.4396 15.6347 14.0742C15.6654 13.6991 15.666 13.2139 15.666 12.5001V11.5001C15.666 11.0398 16.0391 10.6667 16.4993 10.6667C16.9596 10.6667 17.3327 11.0398 17.3327 11.5001V12.5345C17.3327 13.2053 17.3327 13.7589 17.2959 14.2099C17.2576 14.6784 17.1754 15.1089 16.9694 15.5134C16.6498 16.1406 16.1399 16.6505 15.5127 16.9701C15.1082 17.1762 14.6776 17.2583 14.2092 17.2966C13.7582 17.3334 13.2046 17.3334 12.5338 17.3334H5.46493C4.79413 17.3334 4.24049 17.3334 3.7895 17.2966C3.32108 17.2583 2.89049 17.1762 2.48605 16.9701C1.85884 16.6505 1.34891 16.1406 1.02933 15.5134C0.823253 15.1089 0.741116 14.6784 0.702845 14.2099C0.665997 13.7589 0.666006 13.2053 0.666016 12.5345V11.5001C0.666016 11.0398 1.03911 10.6667 1.49935 10.6667Z" fill="#1643E0" />
                                    </svg>
                                    <input onChange={handleFileChange} style={{ marginLeft: '-20px', fontSize: '12px' }} type="file" id="avatar" name="avatar" accept="image/png, image/jpeg, image/jpg, image/svg" />
                                </div>
                            </div>
                        </div>
                        <div className='pv-card d-flex flex-column'>
                            <h6 className='pv-eyebrow' style={{ marginBottom: 'var(--space-2)' }}>TODAY'S STATISTICS</h6>
                            <div className='pv-kv'>
                                <span className='pv-kv__k'>TOTAL RESERVATIONS</span>
                                <span className='pv-kv__v'>{totalNumOfReservations}</span>
                            </div>
                            <div className='pv-kv'>
                                <span className='pv-kv__k'>TOTAL REVENUE</span>
                                <span className='pv-kv__v'>{totalRevenue}</span>
                            </div>
                            <div className='pv-kv'>
                                <span className='pv-kv__k'>VOIDED BILLS</span>
                                <span className='pv-kv__v'>{user?.numberOfVoidedOrders}</span>
                            </div>
                            <div className='pv-kv'>
                                <span className='pv-kv__k'>PAID REVENUE</span>
                                <span className='pv-kv__v'>{totalPaidRevenue}</span>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {
                selectedTab == 'notes' &&
                <div className='mt-4'>
                    <div className='d-flex flex-wrap mt-4 gap-3'>
                        <div className='col-12 col-lg-3 pv-notes-add'>
                            <h6 className='pv-section-title' style={{ marginTop: 0 }}>Add New Notes</h6>
                            <textarea
                                placeholder='Enter a description...'
                                rows={6}
                                className='pv-textarea mt-2'
                                value={newNoteText}
                                onChange={(e) => setNewNoteText(e.target.value)}
                            />
                            <div className='d-flex justify-content-end mt-3'>
                                <button className='pv-btn pv-btn--ghost' onClick={handleSaveNote}>Save Note</button>
                            </div>
                        </div>
                        <div className='col'>
                            <div className='pv-notes-tabs'>
                                {notesTabs.map((tab) => (
                                    <button
                                        key={tab.value}
                                        className={selectedNotesTab === tab.value ? 'pv-notes-tab is-on' : 'pv-notes-tab'}
                                        onClick={() => setSelectedNotesTab(tab.value)}>
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            <div className='pv-notes-scroll'>
                            <div className='pv-notes-grid'>
                                <div className="pv-nc pv-nc--head">
                                    Date
                                </div>
                                <div className="pv-nc pv-nc--head">
                                    Time
                                </div>
                                <div className="pv-nc pv-nc--head">
                                    Notes
                                </div>
                                <div className="pv-nc pv-nc--head">

                                </div>
                                {
                                    selectedNotesTab == 'Active Notes' &&
                                    <>
                                        <>
                                            {user?.notes?.filter((note) => !note.isArchived).map((note) => (
                                                <>
                                                    <React.Fragment>
                                                        <div className='pv-nc'>
                                                            {formatNoteDate(note.date)}
                                                        </div>
                                                        <div className='pv-nc'>
                                                            {formatTime(note.date)}
                                                        </div>
                                                        <div className='pv-nc'>
                                                            {note.note}
                                                        </div>
                                                        <div className='pv-nc' style={{ position: 'relative', justifyContent: 'center' }}>
                                                            <button
                                                                className='pv-iconbtn'
                                                                onClick={() => handleToggleOptions(note.id)}
                                                            >
                                                                <svg width="4" height="16" viewBox="0 0 4 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M1.99935 8.83334C2.45959 8.83334 2.83268 8.46025 2.83268 8.00001C2.83268 7.53977 2.45959 7.16668 1.99935 7.16668C1.53911 7.16668 1.16602 7.53977 1.16602 8.00001C1.16602 8.46025 1.53911 8.83334 1.99935 8.83334Z" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                                                    <path d="M1.99935 3.00001C2.45959 3.00001 2.83268 2.62691 2.83268 2.16668C2.83268 1.70644 2.45959 1.33334 1.99935 1.33334C1.53911 1.33334 1.16602 1.70644 1.16602 2.16668C1.16602 2.62691 1.53911 3.00001 1.99935 3.00001Z" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                                                    <path d="M1.99935 14.6667C2.45959 14.6667 2.83268 14.2936 2.83268 13.8333C2.83268 13.3731 2.45959 13 1.99935 13C1.53911 13 1.16602 13.3731 1.16602 13.8333C1.16602 14.2936 1.53911 14.6667 1.99935 14.6667Z" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                                                                </svg>
                                                            </button>
                                                            {noteOptionsOpen === note.id && (
                                                                <div className='options-menu' ref={noteOptionsRef} style={{ position: 'absolute', right: 0, zIndex: 100 }}>
                                                                    <button onClick={() => handleArchiveNote(note.id)} className='pv-menuitem'>Archive</button>
                                                                    <button onClick={() => handleEditNote(note)} className='pv-menuitem'>Edit</button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </React.Fragment>
                                                </>
                                            ))}
                                        </>
                                    </>

                                }

                                {
                                    selectedNotesTab == 'Archived Notes' &&
                                    <>

                                        <>
                                            {user?.notes?.filter((note) => note.isArchived).map((note) => (
                                                <>
                                                    <React.Fragment>
                                                        <div className='pv-nc'>
                                                            {formatNoteDate(note.date)}
                                                        </div>
                                                        <div className='pv-nc'>
                                                            {formatTime(note.date)}
                                                        </div>
                                                        <div className='pv-nc'>
                                                            {note.note}
                                                        </div>
                                                        <div className='pv-nc'>

                                                        </div>
                                                    </React.Fragment>
                                                </>
                                            ))}
                                        </>
                                    </>
                                }
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {showSpinner && (
                <Spinner />
            )}
            {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
            {confirmConfig && (
                <>
                    <div className="pv-modal-overlay"></div>

                    <DeleteConfirmation
                        title={confirmConfig.title}
                        content={confirmConfig.content}
                        onClose={() => setConfirmConfig(null)}
                        onDelete={() => {
                            confirmConfig.onConfirm();
                            setConfirmConfig(null);
                        }}
                    />
                </>
            )}

            {
                editNoteDrawerOpen &&
                <>
                    <div className="pv-modal-overlay"></div>
                    <EditNotesPopup
                        onClose={() => setEditNoteDrawerOpen(false)}
                        note={selectedNote}
                        onSave={handleSaveEditNote}
                    />
                </>
            }
            {
                showResetPasswordPopup &&
                <>
                    <div className="pv-modal-overlay"></div>
                    <ResetUserPasswordPopup
                        onClose={() => setShowResetPasswordPopup(false)}
                        onSave={handleSavePassword}
                    />
                </>

            }
            {
                showDeactivateUserPopup &&
                <>
                    <div className="pv-modal-overlay"></div>
                    <DeactivateUserPopup onSave={handleDeactivateUser} onClose={() => setShowDeactivateUserPopup(false)} canBeDeactivated={canUserBeDeactivated} />
                </>

            }

            {
                showMyShiftPopup && myShiftData &&
                <>
                    <div className="pv-modal-overlay" onClick={() => setShowMyShiftPopup(false)}></div>
                    <div className="pv-modal">
                        <div className="pv-modal__head">
                            <div className="pv-modal__headicon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M16 13H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M16 17H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M10 9H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <span className="pv-modal__title">MY SHIFT X-REPORT</span>
                            <button className="pv-modal__close" onClick={() => setShowMyShiftPopup(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>

                        <div className="pv-modal__body">
                            <div className="pv-receipt" ref={receiptRef}>
                                {/* Company Header */}
                                <div className="pv-receipt__company">
                                    <div className="pv-receipt__name">{property?.displayName || 'PROPERTY NAME'}</div>
                                    <div className="pv-receipt__sub">Cashier: {user?.firstName} {user?.lastName}</div>
                                </div>

                                {/* Report Title */}
                                <div className="pv-receipt__title">
                                    <div className="pv-receipt__title-main">USER X REPORT</div>
                                    <div className="pv-receipt__title-sub">X# {formatShiftId(myShiftData.shiftId)} &nbsp; {formatDateReport(new Date().toISOString())}</div>
                                </div>

                                <div className="pv-receipt__rule"></div>

                                <div className="pv-receipt__since">
                                    SINCE: Shift started at {formatDateReport(myShiftData.startDate)}
                                </div>

                                <div className="pv-receipt__rule"></div>

                                {/* Sales Section */}
                                <div className="pv-receipt__section">
                                    <div className="pv-receipt__section-title">SALES</div>
                                    <div className="pv-receipt__line">
                                        <span>Gross Sales (excl tax)</span>
                                        <span>{formatCurrency(myShiftData.grossSales)}</span>
                                    </div>
                                    <div className="pv-receipt__line">
                                        <span>Discounts/Promos</span>
                                        <span>{myShiftData.discounts ? `-${formatCurrency(myShiftData.discounts)}` : '0.00'}</span>
                                    </div>
                                    <div className="pv-receipt__line">
                                        <span>Meal Plan Included</span>
                                        <span>{(myShiftData as any).mealPlanDeductions ? `-${formatCurrency((myShiftData as any).mealPlanDeductions)}` : '0.00'}</span>
                                    </div>
                                    <div className="pv-receipt__line">
                                        <span>Returns/Refunds</span>
                                        <span>{myShiftData.returns ? `-${formatCurrency(myShiftData.returns)}` : '0.00'}</span>
                                    </div>
                                    <div className="pv-receipt__line">
                                        <span>Net Sales</span>
                                        <span>{formatCurrency(myShiftData.netSales)}</span>
                                    </div>
                                    <div className="pv-receipt__line">
                                        <span>Tax</span>
                                        <span>{formatCurrency(myShiftData.taxes)}</span>
                                    </div>
                                    <div className="pv-receipt__line">
                                        <span>Service Charge</span>
                                        <span>{formatCurrency(myShiftData.serviceCharge)}</span>
                                    </div>
                                    <div className="pv-receipt__line is-bold">
                                        <span>Total Revenue</span>
                                        <span>{formatCurrency(myShiftData.totalRevenue)}</span>
                                    </div>
                                    <div className="pv-receipt__line is-muted">
                                        <span>Total Receipts</span>
                                        <span>{myShiftData.numberOfReceipts ?? 0}</span>
                                    </div>
                                </div>

                                <div className="pv-receipt__rule"></div>

                                {/* Tenders Section */}
                                <div className="pv-receipt__section">
                                    <div className="pv-receipt__section-title">TENDERS</div>
                                    <div className="pv-receipt__line">
                                        <span>Cash</span>
                                        <span>{formatCurrency(myShiftData.expectedCash)}</span>
                                    </div>
                                    <div className="pv-receipt__line">
                                        <span>Credit Card</span>
                                        <span>{formatCurrency(myShiftData.creditCards)}</span>
                                    </div>
                                    <div className="pv-receipt__line">
                                        <span>Room Charges</span>
                                        <span>{formatCurrency(myShiftData.roomCharges)}</span>
                                    </div>
                                </div>

                                <div className="pv-receipt__rule"></div>

                                {/* Controls Section */}
                                <div className="pv-receipt__section">
                                    <div className="pv-receipt__section-title">CONTROLS</div>
                                    <div className="pv-receipt__center">
                                        Receipts: {myShiftData.numberOfReceipts ?? 0} &nbsp; Voids: {myShiftData.numberOfVoidOrders ?? 0}
                                    </div>
                                    <div className="pv-receipt__center">
                                        First: {myShiftData.firstOrderNumber || '-'}
                                    </div>
                                    <div className="pv-receipt__center">
                                        Last: {myShiftData.lastOrderNumber || '-'}
                                    </div>
                                </div>

                                <div className="pv-receipt__rule"></div>

                                {/* Footer */}
                                <div className="pv-receipt__footer">
                                    *USER SHIFT REPORT - {user?.firstName} {user?.lastName}*
                                </div>
                            </div>
                        </div>

                        <div className="pv-modal__foot">
                            <button className="pv-btn pv-btn--ghost" onClick={handleSaveUserReport}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16L21 8V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Save
                            </button>
                            <button className="pv-btn pv-btn--primary" onClick={handlePrintUserReport}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 9V2H18V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M6 18H4C2.89543 18 2 17.1046 2 16V11C2 9.89543 2.89543 9 4 9H20C21.1046 9 22 9.89543 22 11V16C22 17.1046 21.1046 18 20 18H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18 14H6V22H18V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Print
                            </button>
                        </div>
                    </div>
                </>
            }
        </div>
    );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const styles = StyleSheet.create({
    profilePage: {
        paddingTop: 60,
        paddingLeft: 15,
        paddingRight: 15
    },
    label: {
        marginTop: 8,
        fontSize: 14,
        fontWeight: 500,
        fontFamily: 'Inter'
    },
    thumbsContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginTop: 16,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#EAECF0',
        height: '100%',
        margin: 0,
        padding: 10
    },
    input: {
        borderRadius: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#D0D5DD',
        height: 35
    },
    textarea: {
        borderRadius: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#D0D5DD',
    },

});

export default Profile;